import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { message } from 'antd';
import { fetchMembershipRuleById, mapRuleToFormData } from './data';
import FullPageLoader from '../common/FullPageLoader';
import StepBasicInfo from './steps/StepBasicInfo';
import StepBenefitMechanic from './steps/StepBenefitMechanic';
import StepEligibilityConditions from './steps/StepEligibilityConditions';
import StepProducts from './steps/StepProducts';
import StepReviewAndPublish from './steps/StepReviewAndPublish';

const IconCheck = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" {...props}>
  <path d="M14.2498 2.96875H13.1576C13.0045 2.96875 12.8591 3.03906 12.7654 3.15937L6.3232 11.3203L3.23414 7.40625C3.18741 7.34692 3.12785 7.29895 3.05992 7.26593C2.992 7.23292 2.91748 7.21572 2.84195 7.21562H1.74976C1.64508 7.21562 1.58726 7.33594 1.65133 7.41719L5.93102 12.8391C6.13102 13.0922 6.51539 13.0922 6.71695 12.8391L14.3482 3.16875C14.4123 3.08906 14.3545 2.96875 14.2498 2.96875Z" fill="black" fillOpacity="0.88"/>
</svg>
)

const steps = [
  { path: 'basic-info', label: 'Basic Info', component: StepBasicInfo },
  { path: 'benefit-mechanic', label: 'Benefit & Mechanic', component: StepBenefitMechanic },
  { path: 'eligibility-conditions', label: 'Eligibility Conditions', component: StepEligibilityConditions },
  { path: 'products', label: 'Products (Include/Exclude)', component: StepProducts },
  { path: 'review-and-publish', label: 'Review & Publish', component: StepReviewAndPublish },
];

const createRulesRoute = '/app/membership-rules/new/create-rules';
const initialFormData = {
  ruleName: '',
  storefront: 'UAT',
  ruleDescription: '',
  giftEnabled: true,
  minCartValue: '',
  giftSkus: [],
  giftsPerOrder: '',
  discountEnabled: false,
  discountType: 'percentage',
  discountValue: '',
  ruleType: 'BAU',
  memberType: 'Member',
  activeStartDate: '',
  activeEndDate: '',
  triggerWindowDays: '',
  eligibilityGroups: [
    {
      id: 1,
      type: 'And',
      conditions: [{ id: '1-c1', field: 'email_subscribe', operator: '=', value: 'true' }],
      afterConditions: [],
      children: [],
    },
  ],
  attributeCondition: 'online_is_anniversary > 1',
  redemptionLimit: '',
  redemptionLimitUnlimited: false,
  priority: '',
  listType: 'Included',
  skuSource: 'Custom SKUs',
  includedSkus: [],
  excludedSkus: [],
};

const MembershipRuleWizard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Path looks like /app/membership-rules/:ruleId/:mode (view|edit) when no nav state was passed.
  const pathSegments = location.pathname.replace(/^\/app\/membership-rules\/?/, '').split('/').filter(Boolean);
  const [urlRuleId, urlMode] = pathSegments;
  const mode = location.state?.mode || urlMode;
  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit';
  const [selectedRule, setSelectedRule] = useState(location.state?.rule || null);
  // View starts at Review; Edit starts at Basic Info with the existing rule loaded.
  const [activeStepIndex, setActiveStepIndex] = useState(isViewMode ? steps.length - 1 : 0);
  const [maxUnlockedStep, setMaxUnlockedStep] = useState(selectedRule && !isViewMode ? steps.length - 1 : isViewMode ? steps.length - 1 : 0);
  const [formData, setFormData] = useState(() => selectedRule
    ? mapRuleToFormData(selectedRule, initialFormData)
    : initialFormData);
  const [loadingRule, setLoadingRule] = useState(false);
  const savedRuleId = selectedRule?.rule_id ?? urlRuleId;

  useEffect(() => {
    if (window.location.pathname.endsWith('/new')) {
      navigate(createRulesRoute, { replace: true });
    }
  }, [navigate]);

  // View/Edit always fetch the full rule detail — the table's listing data (location.state.rule)
  // only carries summary fields (no gifts/discount/conditions), so it's not enough on its own.
  useEffect(() => {
    if (!urlRuleId || urlRuleId === 'new' || (mode !== 'view' && mode !== 'edit')) return;
    let isMounted = true;
    setLoadingRule(true);
    fetchMembershipRuleById(urlRuleId)
      .then((rule) => {
        if (!isMounted || !rule) return;
        setSelectedRule(rule);
        setFormData(mapRuleToFormData(rule, initialFormData));
        if (mode !== 'view') setMaxUnlockedStep(steps.length - 1);
      })
      .finally(() => {
        if (isMounted) setLoadingRule(false);
      });
    return () => {
      isMounted = false;
    };
  }, [urlRuleId, mode]);

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const currentStepIndex = Math.min(Math.max(activeStepIndex, 0), steps.length - 1);
  const currentStep = steps[currentStepIndex];
  const CurrentStepComponent = currentStep.component;

  const goToStep = (index) => {
    const step = steps[index];
    if (!step) return;
    if (index > maxUnlockedStep) return;
    setActiveStepIndex(index);
  };

  const moveToNext = () => {
    const nextIndex = currentStepIndex + 1;
    const nextStep = steps[nextIndex];
    if (!nextStep) return;
    setMaxUnlockedStep((prev) => Math.max(prev, nextIndex));
    setActiveStepIndex(nextIndex);
  };

  const moveToPrevious = (targetIndex) => {
    const prevIndex = Number.isInteger(targetIndex) ? targetIndex : currentStepIndex - 1;
    if (prevIndex < 0) {
      navigate('/app/membership-rules');
      return;
    }
    setActiveStepIndex(prevIndex);
  };

  return (
    <div className='MembershipDashboard'>
      <FullPageLoader isloader={loadingRule} />
      <div className='rule-nav'>
        <div className='rule-left-nav' >
          <button
            type="button"
            onClick={() => navigate('/app/membership-rules')}
            className='btn-s w-164'
          >
            ← Back to All Rules
          </button>

          {(isViewMode ? steps.slice(-1) : steps).map((step, visibleIndex) => {
            const index = isViewMode ? steps.length - 1 : visibleIndex;
            const isDone = index < maxUnlockedStep;
            const isActive = index === currentStepIndex;
            return (
              <button
                key={step.path}
                type="button"
                onClick={() => goToStep(index)}
                className={`rule-left-nav-btn ${isActive ? 'rule-btn-active' : ''}`}
                style={{
                  cursor: index <= maxUnlockedStep ? 'pointer' : 'not-allowed',
                  opacity: index <= maxUnlockedStep ? 1 : 0.5,
                }}
              >
                {isDone && 
                    <span className='Icon-check'>
                        <IconCheck />
                    </span>
                }
                <span style={{ fontSize: 14 }}>{step.label}</span>
              </button>
            );
          })}
        </div>

        <div className='rule-right-content'>
          <CurrentStepComponent
            onNext={moveToNext}
            onBack={moveToPrevious}
            formData={formData}
            updateField={updateField}
            // View renders the Review step without edit or publish controls.
            readOnly={isViewMode}
            // Set while editing a saved rule: Save as Draft / Publish Rule update it instead of creating.
            ruleId={isEditMode ? savedRuleId : null}
          />
        </div>
      </div>
    </div>
  );
};

export default MembershipRuleWizard;
